import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [followUpNotifications, setFollowUpNotifications] = useState([]);
	const [slaNotifications, setSlaNotifications] = useState([]);
	const [automationNotifications, setAutomationNotifications] = useState([]);
	const [internalChatNotifications, setInternalChatNotifications] = useState([]);
	const notifiedFollowUpsRef = useRef(new Set(JSON.parse(localStorage.getItem("notifiedFollowUps") || "[]")));
	const notifiedSlaRef = useRef(new Set(JSON.parse(localStorage.getItem("notifiedSla") || "[]")));

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		const loadFollowUps = async () => {
			try {
				const { data } = await api.get("/follow-ups", { params: { status: "pending" } });
				const limit = new Date(Date.now() + 15 * 60 * 1000);
				const due = data.filter(item => new Date(item.dueAt) <= limit);
				setFollowUpNotifications(due);
				due.forEach(item => {
					if (notifiedFollowUpsRef.current.has(item.id) || !("Notification" in window) || Notification.permission !== "granted") return;
					const overdue = new Date(item.dueAt) < new Date();
					const notification = new Notification(overdue ? "Retorno atrasado" : "Retorno próximo", {
						body: `${item.ticket?.contact?.name || "Contato"} — ${item.note}`,
						tag: `follow-up-${item.id}`
					});
					notification.onclick = event => {
						event.preventDefault(); window.focus(); historyRef.current.push(`/tickets/${item.ticketId}`);
					};
					notifiedFollowUpsRef.current.add(item.id);
					localStorage.setItem("notifiedFollowUps", JSON.stringify([...notifiedFollowUpsRef.current].slice(-500)));
					soundAlertRef.current?.();
				});
			} catch (_) {}
		};
		loadFollowUps(); const timer = setInterval(loadFollowUps, 60000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		const loadSla = async () => {
			try {
				const { data } = await api.get("/operations-dashboard");
				const alerts = data.slaAlerts || [];
				setSlaNotifications(alerts);
				alerts.forEach(item => {
					const key = `${item.ticketId}-${item.sla.status}`;
					if (notifiedSlaRef.current.has(key) || !("Notification" in window) || Notification.permission !== "granted") return;
					const notification = new Notification(item.sla.status === "breached" ? "SLA vencido" : "SLA próximo do vencimento", {
						body: `${item.contact?.name || "Contato"} — ${item.queue?.name || "Atendimento"}`,
						tag: `sla-${key}`
					});
					notification.onclick = event => { event.preventDefault(); window.focus(); historyRef.current.push(`/tickets/${item.ticketId}`); };
					notifiedSlaRef.current.add(key);
					localStorage.setItem("notifiedSla", JSON.stringify([...notifiedSlaRef.current].slice(-500)));
					soundAlertRef.current?.();
				});
			} catch (_) {}
		};
		loadSla(); const timer = setInterval(loadSla, 60000); return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", data => {
			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		socket.on("automationAlert", data => {
			setAutomationNotifications(current => [{ ...data, id: `${Date.now()}-${data.ticketId}` }, ...current].slice(0, 20));
			if ("Notification" in window && Notification.permission === "granted") {
				const notification = new Notification(data.ruleName || "Automação", { body: data.message, tag: `automation-${data.ticketId}-${data.ruleName}` });
				notification.onclick = event => { event.preventDefault(); window.focus(); historyRef.current.push(`/tickets/${data.ticketId}`); };
			}
			soundAlertRef.current?.();
		});

		socket.on("internalChatNotification", data => {
			if (Number(data.message?.senderId) === Number(user?.id)) return;
			setInternalChatNotifications(current => [{ ...data, id: `${Date.now()}-${data.message?.id}` }, ...current].slice(0, 30));
			if ("Notification" in window && Notification.permission === "granted") {
				const notification = new Notification(`Mensagem interna de ${data.message?.sender?.name || "Usuário"}`, { body: data.message?.body, tag: `internal-chat-${data.chatId}` });
				notification.onclick = event => { event.preventDefault(); window.focus(); historyRef.current.push("/internal-chat"); };
			}
			soundAlertRef.current?.();
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length + followUpNotifications.length + slaNotifications.length + automationNotifications.length + internalChatNotifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{followUpNotifications.map(item => <ListItem button key={`follow-up-${item.id}`} onClick={() => { handleClickAway(); history.push(`/tickets/${item.ticketId}`); }}>
						<ListItemText primary={`${new Date(item.dueAt) < new Date() ? "Retorno atrasado" : "Retorno próximo"}: ${item.ticket?.contact?.name || "Contato"}`} secondary={`${new Date(item.dueAt).toLocaleString("pt-BR")} — ${item.note}`} />
					</ListItem>)}
					{slaNotifications.map(item => <ListItem button key={`sla-${item.ticketId}`} onClick={() => { handleClickAway(); history.push(`/tickets/${item.ticketId}`); }}>
						<ListItemText primary={`${item.sla.status === "breached" ? "SLA vencido" : "SLA próximo"}: ${item.contact?.name || "Contato"}`} secondary={`${item.queue?.name || "Atendimento"} — ${Math.abs(item.sla.remainingMinutes)} minutos`} />
					</ListItem>)}
					{automationNotifications.map(item => <ListItem button key={item.id} onClick={() => { handleClickAway(); history.push(`/tickets/${item.ticketId}`); }}><ListItemText primary={item.ruleName || "Automação"} secondary={item.message} /></ListItem>)}
					{internalChatNotifications.map(item => <ListItem button key={item.id} onClick={() => { handleClickAway(); history.push("/internal-chat"); }}><ListItemText primary={`Chat interno — ${item.message?.sender?.name || "Usuário"}`} secondary={item.message?.body} /></ListItem>)}
					{notifications.length === 0 && followUpNotifications.length === 0 && slaNotifications.length === 0 && automationNotifications.length === 0 && internalChatNotifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
